"use client";

/**
 * The one ask on these pages.
 *
 * This replaces a three-offer block that sat under the output: email me the
 * markup, scan my whole site, send the plugin link. Three asks stacked in a
 * panel is a menu, and a menu at the end of a task is something you skip. One
 * ask, in the band treatment the GEO checker closes with, is the same product
 * speaking with one voice in both places.
 *
 * The ask is deliberately the honest next problem rather than the biggest one we
 * could pitch: you have just hand-built markup for a single page, and the thing
 * that actually does not scale is doing it again for every other page and
 * keeping it current.
 *
 * Nothing above this was gated, which is what earns it the right to be here.
 *
 * The ask used to be an email capture that posted to /api/schema-lead and mailed
 * the install link. It is now a straight send to /pricing, so these pages hand
 * the visitor to the plan choice instead of collecting an address first. That
 * route has no other caller — see the note in this component's commit.
 */

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { AnalyticsEvents, trackEvent } from "@/lib/analytics/index";
import Reveal from "../Reveal";

type Props = {
  /** Which route this is, reported on the CTA click. */
  source: string;
};

export default function SchemaBottomCta({ source }: Props) {
  return (
    <div className="w-full bg-black px-6 py-20 lg:py-28">
      <Reveal>
        <div className="mx-auto flex w-full max-w-[680px] flex-col items-center text-center">
          <span className="inline-flex items-center rounded-full bg-nws-yellow px-[11px] py-[5px] font-sans text-[10px] font-medium uppercase tracking-[0.09em] text-[#1a1a1a] lg:px-[15px] lg:py-[7px] lg:text-xs lg:tracking-[0.1em]">
            WordPress plugin
          </span>

          <h2 className="mt-6 font-sans text-[30px] font-medium leading-[1.1] tracking-[-0.03em] text-white lg:text-[40px]">
            Want this kept up to date across your whole site?
          </h2>
          <p className="mt-5 max-w-[46ch] font-sans text-[16px] leading-relaxed text-white/70">
            You have just built markup for one page by hand. Our plugin does every page, wires
            them into one graph, and keeps them current as your content changes.
          </p>

          <Link
            href="/pricing"
            onClick={() =>
              trackEvent(AnalyticsEvents.BUTTON_CLICK, {
                button_name: "Start free trial",
                location: `Schema Bottom CTA - ${source}`,
              })
            }
            className="group mt-9 inline-flex h-[56px] items-center gap-2 rounded-full bg-white px-8 font-sans text-[15px] font-semibold text-black transition-colors hover:bg-white/90 active:scale-[0.98]"
          >
            Start free trial
            <ArrowRight
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}
